import { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from 'utils/@reduxjs/toolkit';
import { createRoutine } from 'redux-saga-routines';
import { useInjectReducer, useInjectSaga } from 'utils/redux-injectors';
import { appSaga } from './saga';
import {
  AppState,
  DialogNotiState,
  I_Layout,
  I_User,
  I_Address,
  I_BusinessPartnerInfoData,
  I_AccountBelongAgentData,
  I_BusinessPartnerBanlceData,
  AuthDialogInfo,
  MaintenanceData,
} from './types';
import { objectType } from 'types';
import { setCookie } from 'utils/cookies';
import { TYPE_COOKIE } from 'utils/constants';

export const GET_ME = createRoutine('app/getMe');
export const GET_LAYOUT = createRoutine('app/getLayout');
export const GET_TRANS = createRoutine('app/getTranslations');
export const GET_NATIONAL = createRoutine('app/getNational');
export const GET_PROVINCE = createRoutine('app/getProvince');
export const GET_DISTRICT = createRoutine('app/getDistrict');
export const GET_BUSINESS_PARTNER = createRoutine('app/getBusinessPartner');
export const GET_ACC_BELONG_AGENT = createRoutine('app/getAccListBelongAgent');
export const GET_BUSINESS_PARTNER_BANLCE = createRoutine(
  'app/getBusinessPartnerBanlce',
);

export const LOGIN = createRoutine('app/login');
export const LOGOUT = createRoutine('app/logout');
export const REGISTER = createRoutine('app/register');

export const MAINTENANCE = createRoutine('app/maintenance');

const getStateAuthModal = (
  isShow = false,
  type?: 'LOGIN' | 'REGISTER' | 'FORGOT_PASS' | 'CHANGE_PASS',
): {
  isShowDialogLogin: boolean;
  isShowDialogRegister: boolean;
  isShowDialogForgotPass: boolean;
  isShowDialogChangePass: boolean;
} => {
  switch (type) {
    case 'LOGIN':
      return {
        isShowDialogLogin: isShow,
        isShowDialogRegister: false,
        isShowDialogForgotPass: false,
        isShowDialogChangePass: false,
      };
    case 'REGISTER':
      return {
        isShowDialogLogin: false,
        isShowDialogRegister: isShow,
        isShowDialogForgotPass: false,
        isShowDialogChangePass: false,
      };
    case 'FORGOT_PASS':
      return {
        isShowDialogLogin: false,
        isShowDialogRegister: false,
        isShowDialogForgotPass: isShow,
        isShowDialogChangePass: false,
      };
    case 'CHANGE_PASS':
      return {
        isShowDialogLogin: false,
        isShowDialogRegister: false,
        isShowDialogForgotPass: false,
        isShowDialogChangePass: isShow,
      };

    default:
      return {
        isShowDialogLogin: false,
        isShowDialogRegister: false,
        isShowDialogForgotPass: false,
        isShowDialogChangePass: false,
      };
  }
};

export const initialState: AppState = {
  loading: false,
  success: false,
  error: false,
  infoUser: undefined,
  translations: undefined,
  isLogged: undefined,
  language: 'vi',
  layout: {
    header: undefined,
    footer: undefined,
  },
  isShowDialogLogin: false,
  isShowDialogRegister: false,
  isShowDialogForgotPass: false,
  isShowDialogChangePass: false,
  authDialogInfo: null,
  dialogNotiState: {
    isShow: false,
    buttons: [],
    type: 'SUCCESS',
    title: '',
    content: '',
    maskClosable: true,
  },
  businessPartner: {
    info: {
      data: null,
      loading: false,
      error: false,
      success: false,
    },
    accountList: {
      data: null,
      loading: false,
      error: false,
      success: false,
    },
    banlce: {
      data: null,
      loading: false,
      error: false,
      success: false,
    },
  },
  DOMRect: {
    header: {},
  },
  maintenance: {
    loading: false,
    displayReminder: false,
    data: null,
  },
};

const slice = createSlice({
  name: 'app',
  initialState,
  reducers: {
    openDialogLogin(state) {
      const newState = getStateAuthModal(true, 'LOGIN');
      const keys = Object.keys(newState);
      for (let index = 0; index < keys.length; index++) {
        const key = keys[index];
        state[key] = newState[key];
      }
    },

    closeDialogAuth(state) {
      const newState = getStateAuthModal(false);
      const keys = Object.keys(newState);
      for (let index = 0; index < keys.length; index++) {
        const key = keys[index];
        state[key] = newState[key];
      }
    },

    openDialogRegister(state) {
      const newState = getStateAuthModal(true, 'REGISTER');
      const keys = Object.keys(newState);
      for (let index = 0; index < keys.length; index++) {
        const key = keys[index];
        state[key] = newState[key];
      }
    },

    onChangeLanguage(state, action: PayloadAction<'vi' | 'en'>) {
      state.language = action.payload;
      setCookie(TYPE_COOKIE.LANGUAGE, action.payload as string);
    },

    changeIsLogged(state, action: PayloadAction<{ isLogged: boolean }>) {
      state.isLogged = action.payload.isLogged;
    },

    changeStateLoading(state, action: PayloadAction<{ loading: boolean }>) {
      state.loading = action.payload.loading;
    },

    setInfoUser(state, action: PayloadAction<I_User | undefined>) {
      state.infoUser = state.infoUser
        ? { ...state.infoUser, ...action.payload }
        : action.payload;
    },

    logout(state, action?: PayloadAction<{ callback?: () => void }>) {
      state.infoUser = initialState.infoUser;
      state = initialState;
    },

    resetState(state) {
      Object.keys(state).forEach(item => {
        state[item] = initialState[item];
      });
    },

    clearAuthDialogInfo(state) {
      state.authDialogInfo = null;
    },

    openDialogForgotPass(state, action: PayloadAction<AuthDialogInfo | null>) {
      state.authDialogInfo = action.payload;

      const newState = getStateAuthModal(true, 'FORGOT_PASS');
      const keys = Object.keys(newState);
      for (let index = 0; index < keys.length; index++) {
        const key = keys[index];
        state[key] = newState[key];
      }
    },
    openDialogChangePass(state) {
      const newState = getStateAuthModal(true, 'CHANGE_PASS');
      const keys = Object.keys(newState);
      for (let index = 0; index < keys.length; index++) {
        const key = keys[index];
        state[key] = newState[key];
      }
    },
    openDialogNoti(state, action: PayloadAction<DialogNotiState>) {
      state.dialogNotiState = action.payload;
    },

    closeDialogNoti(state) {
      state.dialogNotiState = {
        isShow: false,
        buttons: [],
        type: 'SUCCESS',
        title: '',
        content: '',
      };
    },

    updateAccBelongAgentData(
      state,
      action: PayloadAction<I_AccountBelongAgentData>,
    ) {
      const { id: payloadId } = action.payload;
      state.businessPartner.accountList.data =
        state.businessPartner.accountList.data?.map(i => {
          if (i.id === payloadId) return action.payload;
          return { ...i };
        })!;
    },

    setHeaderDOMRect(state, action: PayloadAction<DOMRect>) {
      state.DOMRect.header = action.payload;
    },

    setDisplayMaintenanceReminder(state, action: PayloadAction<boolean>) {
      state.maintenance.displayReminder = action.payload;
    },
  },
  extraReducers: {
    /**
     * !GET_LAYOUT
     */
    [GET_LAYOUT.TRIGGER]: state => {
      state.loading = true;
      // state.success = false;
      state.error = false;
    },
    [GET_LAYOUT.SUCCESS]: (state, action: PayloadAction<I_Layout>) => {
      state.layout = action.payload;
      state.loading = false;
      // state.success = true;
      state.error = false;
    },
    [GET_LAYOUT.FAILURE]: state => {
      state.loading = false;
      // state.success = false;
      state.error = true;
    },
    /**
     * !GET_ME
     */
    [GET_ME.TRIGGER]: state => {
      state.loading = true;
      // state.success = false;
      state.error = false;
    },
    [GET_ME.SUCCESS]: (state, action: PayloadAction<I_User>) => {
      state.infoUser = action.payload;
      const allowBuyShortTerm =
        !!action.payload?.businessPartner?.allowBuyShortTerm;
      const bpLongTerm =
        String(action.payload?.businessPartner?.bpLongTerm) === '01';

      let ticketType: any;

      if (allowBuyShortTerm && bpLongTerm) {
        ticketType = 0;
      } else if (bpLongTerm) {
        ticketType = 1;
      } else {
        ticketType = 0;
      }

      sessionStorage.setItem('ticketType', JSON.stringify(ticketType));

      state.loading = false;
      // state.success = true;
      state.error = false;
    },
    [GET_ME.FAILURE]: state => {
      state.loading = false;
      // state.success = false;
      state.error = true;
    },

    /**
     * ! GET TRANS
     */
    [GET_TRANS.TRIGGER]: state => {
      state.loading = true;
      state.success = false;
      state.error = false;
    },
    [GET_TRANS.SUCCESS]: (state, action: PayloadAction<objectType>) => {
      state.translations = action.payload;
      state.loading = false;
      state.success = true;
      state.error = false;
    },
    [GET_TRANS.FAILURE]: state => {
      state.loading = false;
      state.success = false;
      state.error = true;
    },

    /**
     * ! GET NATIONAL
     */
    [GET_NATIONAL.TRIGGER]: state => {},
    [GET_NATIONAL.SUCCESS]: (state, action: PayloadAction<I_Address[]>) => {
      state.national = action.payload;
    },
    [GET_NATIONAL.FAILURE]: state => {},

    /**
     * ! GET PROVINCE
     */
    [GET_PROVINCE.TRIGGER]: state => {},
    [GET_PROVINCE.SUCCESS]: (state, action: PayloadAction<I_Address[]>) => {
      state.province = action.payload;
    },
    [GET_PROVINCE.FAILURE]: state => {},

    /**
     * ! GET DISTRICT
     */
    [GET_DISTRICT.TRIGGER]: state => {},
    [GET_DISTRICT.SUCCESS]: (state, action: PayloadAction<I_Address[]>) => {
      state.district = action.payload;
    },
    [GET_DISTRICT.FAILURE]: state => {},

    /**
     * ! GET BUSINESS PARTNER INFO
     */
    [GET_BUSINESS_PARTNER.TRIGGER]: state => {
      state.businessPartner.info.loading = true;
      state.businessPartner.info.success = false;
      state.businessPartner.info.error = false;
    },
    [GET_BUSINESS_PARTNER.SUCCESS]: (
      state,
      action: PayloadAction<I_BusinessPartnerInfoData>,
    ) => {
      state.businessPartner.info.data = action.payload;
      state.businessPartner.info.loading = false;
      state.businessPartner.info.success = true;
      state.businessPartner.info.error = false;
    },
    [GET_BUSINESS_PARTNER.FAILURE]: state => {
      state.businessPartner.info.loading = false;
      state.businessPartner.info.success = false;
      state.businessPartner.info.error = true;
    },

    /**
     * ! GET ACC LIST BELONG AGENT
     */
    [GET_ACC_BELONG_AGENT.TRIGGER]: state => {
      state.businessPartner.accountList.loading = true;
      state.businessPartner.accountList.success = false;
      state.businessPartner.accountList.error = false;
    },
    [GET_ACC_BELONG_AGENT.SUCCESS]: (
      state,
      action: PayloadAction<I_AccountBelongAgentData[]>,
    ) => {
      state.businessPartner.accountList.data = action.payload;
      state.businessPartner.accountList.loading = false;
      state.businessPartner.accountList.success = true;
      state.businessPartner.accountList.error = false;
    },
    [GET_ACC_BELONG_AGENT.FAILURE]: state => {
      state.businessPartner.accountList.loading = false;
      state.businessPartner.accountList.success = false;
      state.businessPartner.accountList.error = true;
    },

    /**
     * ! GET BUSINESS PARTNER BANLCE
     */
    [GET_BUSINESS_PARTNER_BANLCE.TRIGGER]: state => {
      state.businessPartner.banlce.loading = true;
      state.businessPartner.banlce.success = false;
      state.businessPartner.banlce.error = false;
    },
    [GET_BUSINESS_PARTNER_BANLCE.SUCCESS]: (
      state,
      action: PayloadAction<I_BusinessPartnerBanlceData>,
    ) => {
      state.businessPartner.banlce.data = action.payload;
      state.businessPartner.banlce.loading = false;
      state.businessPartner.banlce.success = true;
      state.businessPartner.banlce.error = false;
    },
    [GET_BUSINESS_PARTNER_BANLCE.FAILURE]: state => {
      state.businessPartner.banlce.loading = false;
      state.businessPartner.banlce.success = false;
      state.businessPartner.banlce.error = true;
    },

    /**
     * ! GET MAINTENANCE
     */
    [MAINTENANCE.TRIGGER]: state => {
      state.maintenance.loading = true;
    },
    [MAINTENANCE.SUCCESS]: (state, action: PayloadAction<MaintenanceData>) => {
      state.maintenance.loading = false;
      state.maintenance.data = action.payload;
    },
    [MAINTENANCE.FAILURE]: state => {
      state.maintenance.loading = false;
      state.maintenance.data = null;
    },
  },
});

export const { actions: appActions } = slice;

export const useAppSlice = () => {
  useInjectReducer({ key: slice.name, reducer: slice.reducer });
  useInjectSaga({ key: slice.name, saga: appSaga });
  return { actions: slice.actions };
};
